/**
 * Live integration broadcasts — a Slack connection or destination registered,
 * edited, paused or deleted, a Jenkins instance or job binding changed by an
 * administrator, a capability's evidence moving, or a connection's traffic
 * moving — delivered through the shared `global_events` room (PRD-0359 D-14,
 * PRD-0401 D-13, PRD-0402 D-24).
 *
 * The room has exactly one entry point, a client-emitted `subscribe_global`,
 * and `websocketManager.subscribe()` only attaches a local handler — it never
 * sends the join and never replays it after a reconnect — so the Integrations
 * console subscribes through this domain or receives nothing.
 */

// The leaf rather than a barrel: INTEGRATION_EVENTS is a runtime value, and
// the leaf keeps it out of every other type module's graph.
import {
  INTEGRATION_EVENTS,
  type IntegrationEventName,
  type IntegrationEventPayload,
} from '@/types/integrations/foundation'
import { WebSocketClient } from '../client'
import { SubscriptionRegistry } from '../subscriptions'

/** What one dispatch carries: which broadcast fired, and its payload. */
type IntegrationDispatch = [IntegrationEventName, IntegrationEventPayload]

export class IntegrationsLiveApi {
  private readonly events: SubscriptionRegistry<null, IntegrationDispatch>

  constructor(private readonly client: WebSocketClient) {
    this.events = new SubscriptionRegistry({
      // Joins the shared `global_events` room; subscribe_global is idempotent
      // server-side. There is deliberately no leave — other domains share the
      // room, and leaving would silence their subscribers.
      join: () => this.client.emitWhenConnected('subscribe_global', {}),
    })
  }

  /**
   * Subscribe to every integration broadcast through one callback, which
   * receives the event name with the payload: the consumer routes cache
   * invalidation by the name.
   */
  subscribeToIntegrationEvents(
    callback: (
      event: IntegrationEventName,
      payload: IntegrationEventPayload
    ) => void
  ): () => void {
    if (!this.client.hasSocket()) {
      console.warn('WebSocket not connected. Call connect() first.')
      return () => {}
    }

    for (const event of INTEGRATION_EVENTS) {
      this.client.attachOnce(event, (raw) => {
        // Socket.IO hands every argument through as `unknown`; these payloads
        // are identity-only routing data that is never rendered, so narrowing
        // by event name is the whole contract.
        this.events.dispatch(null, [event, raw as IntegrationEventPayload])
      })
    }

    return this.events.subscribe(null, ([name, payload]) => callback(name, payload))
  }

  /** Re-join global_events for every live listener after a reconnect. */
  replay(): void {
    this.events.replay()
  }

  clear(): void {
    this.events.clear()
  }
}
